import { CURRENCY, Moment, TransactionStatus } from '~/types'

export enum CSV_FORMAT_VERSION {
  V3 = 'V3',
  V4 = 'V4',
}

export enum TRANSACTIONS_TAB_KEY {
  XFERS_ACCOUNT = 'xfers_account',
  XFERS_WALLET = 'xfers_wallet',
}

export enum TRANSACTION_TYPE {
  BANK_TRANSFER_IN = 'Deposit',
  BANK_TRANSFER_OUT = 'Withdrawal',
  BLOCKCHAIN_TRANSFER_IN = 'Stablecoin Deposit',
  BLOCKCHAIN_TRANSFER_OUT = 'Stablecoin Withdrawal',
  MINT = 'Mint',
  STABLECOIN_SWAP = 'Stablecoin Swap',
  PAYMENT_TRANSFER = 'Payment/Transfer',
  EARN_ADD_FUNDS = 'Add funds',
  EARN_REMOVE_FUNDS = 'Remove funds',
  OTC = 'Otc',
  ADMIN_TRANSFER = 'Admin Transfer',
}

export enum EXPORT_MODE {
  FUNDING,
  SWAP,
  ALL,
}

export const TRANSACTION_TYPE_TITLE: Record<string, string> = {
  [TRANSACTION_TYPE.BANK_TRANSFER_IN]: 'Bank Transfer In',
  [TRANSACTION_TYPE.BANK_TRANSFER_OUT]: 'Bank Transfer Out',
  [TRANSACTION_TYPE.BLOCKCHAIN_TRANSFER_IN]: 'Blockchain Transfer In',
  [TRANSACTION_TYPE.BLOCKCHAIN_TRANSFER_OUT]: 'Blockchain Transfer Out',
  [TRANSACTION_TYPE.MINT]: 'Mint',
  [TRANSACTION_TYPE.STABLECOIN_SWAP]: 'Swap',
  [TRANSACTION_TYPE.EARN_ADD_FUNDS]: 'Add funds',
  [TRANSACTION_TYPE.EARN_REMOVE_FUNDS]: 'Remove funds',
  [TRANSACTION_TYPE.OTC]: 'OTC Trade',
  [TRANSACTION_TYPE.ADMIN_TRANSFER]: 'Admin Transfer',
}

export enum TRANSACTION_TYPE_TAB {
  FUNDING = 'funding',
  SWAP = 'swap',
  OTC = 'otc',
}

export enum FUNDING_TRANSACTION_TYPE_TAB {
  ALL_FUNDING = 'all_funding',
  ACTION_NEEDED = 'action_needed',
}

export type TransactionAmount = {
  prefix: string
  postfix: string
}

export type TransactionDescription = Record<string, string>

export type Transaction = {
  key: number
  amount: TransactionAmount
  transactionId: string
  createdAt: string
  method: string
  transactionStatus: TransactionStatus
  transactionType: TRANSACTION_TYPE
  description: TransactionDescription
  fee: string
  netAmount: TransactionAmount
  currency: { source: string; destination: string }
  tax?: string
  accountHolderName?: string
  transactionHash?: string | null
  transactionHashUrl?: string | null
  blockedReasons: string | null
  network: string | null
  isDeclarationRequired: boolean | null
  metadata?: Record<string, string>
}

export type FilterOptions = {
  currencySymbols: Array<{ label: string; value: CURRENCY }>
  transactionTypes: Array<{ label: string; value: string }>
  transactionStatuses: Array<{ label: string; value: string }>
  sortTypes: Array<{ label: string; value: string }>
  accountTypes: Array<{ label: string; value: string }> | undefined
}

export type FilterValues = {
  currencySymbols: CURRENCY[]
  startDate: Moment | string
  endDate: Moment | string
  transactionStatuses: string[]
  transactionTypes: string[]
  sortType: string
  accountIds: number[]
  currentPage: number
  pageLimit: number
  totalCount: number
  searchString: string
  contractsExist: boolean
  transactions: Transaction[] | undefined
  isTxnLoading: boolean
  isPaginationLoading: boolean
  isTravelRule: boolean
  [key: string]: FilterValuesObject[keyof FilterValuesObject]
}

export type FilterValuesObject = {
  [filterKey: string]:
    | Moment
    | string[]
    | number[]
    | string
    | number
    | Transaction[]
    | boolean
    | undefined
    | CURRENCY
    | null
}

export enum TRANSACTIONS_ACTIONS {
  SET_IS_TXN_LOADING = 'TRANSACTIONS_ACTIONS.SET_IS_TXN_LOADING',
  SET_IS_FILTER_LOADING = 'TRANSACTIONS_ACTIONS.SET_IS_FILTER_LOADING',
  SET_IS_PAGINATION_LOADING = 'TRANSACTIONS_ACTIONS.SET_IS_PAGINATION_LOADING',
  SET_CSV_DOWNLOAD_COMPLETE = 'TRANSACTIONS_ACTIONS.SET_CSV_DOWNLOAD_COMPLETE',
  GET_TRANSACTIONS = 'TRANSACTIONS_ACTIONS.GET_TRANSACTIONS',
  GET_TRANSACTIONS_TOTAL_COUNT = 'TRANSACTIONS_ACTIONS.GET_TRANSACTIONS_TOTAL_COUNT',
  GET_TRANSACTION_FILTER_OPTIONS = 'TRANSACTIONS_ACTIONS.GET_TRANSACTION_FILTER_OPTIONS',
  SET_FILTERS = 'TRANSACTIONS_ACTIONS.SET_FILTERS',
  RESET_CURRENT_PAGE = 'TRANSACTIONS_ACTIONS.RESET_CURRENT_PAGE',
  SET_CSV_FORMAT_VERSION = 'TRANSACTIONS_ACTIONS.SET_CSV_FORMAT_VERSION',
  SWITCH_TRANSACTIONS_TAB = 'TRANSACTIONS_ACTIONS.SWITCH_TRANSACTIONS_TAB',
  GET_TRANSACTION_TAB_OPTIONS = 'TRANSACTIONS_ACTIONS.GET_TRANSACTION_TAB_OPTIONS',
  SET_LATEST_PERSONAL_TRANSACTIONS = 'TRANSACTIONS_ACTIONS.SET_LATEST_PERSONAL_TRANSACTIONS',
  SET_TRANSACTION_TYPE_TAB = 'TRANSACTIONS_ACTIONS.SET_TRANSACTION_TYPE_TAB',
  SET_FUNDING_TRANSACTION_TYPE_TAB = 'TRANSACTIONS_ACTIONS.SET_FUNDING_TRANSACTION_TYPE_TAB',
  GET_TRAVEL_RULE_COMPLIANCE_BLOCKER_TOTAL_COUNT = 'TRANSACTIONS_ACTIONS.GET_TRAVEL_RULE_COMPLIANCE_BLOCKER_TOTAL_COUNT',
  SET_IS_TRANSACTION_TIMEZONE_CONTROLLED = 'TRANSACTIONS_ACTIONS.SET_IS_TRANSACTION_TIMEZONE_CONTROLLED',
  SET_IS_USER_HAS_DEPOSIT = 'TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_DEPOSIT',
  SET_IS_USER_HAS_FIAT_DEPOSIT = 'TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_FIAT_DEPOSIT',
  SET_IS_USER_HAS_STABLECOIN_DEPOSIT = 'TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_STABLECOIN_DEPOSIT',
  SET_IS_USER_HAS_FIAT_WITHDRAWAL = 'TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_FIAT_WITHDRAWAL',
  SET_IS_USER_HAS_STABLECOIN_WITHDRAWAL = 'TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_STABLECOIN_WITHDRAWAL',
}

type SetIsTxnLoading = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_TXN_LOADING
  isTxnLoading: boolean
}

type SetIsUserHasDeposit = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_DEPOSIT
  isUserHasDeposit: boolean
}

type SetIsUserHasFiatDeposit = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_FIAT_DEPOSIT
  isUserHasFiatDeposit: boolean
}

type SetIsUserHasStablecoinDeposit = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_STABLECOIN_DEPOSIT
  isUserHasStablecoinDeposit: boolean
}

type SetIsUserHasStablecoinWithdrawal = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_STABLECOIN_WITHDRAWAL
  isUserHasStablecoinWithdrawal: boolean
}

type SetIsUserHasFiatWithdrawal = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_USER_HAS_FIAT_WITHDRAWAL
  isUserHasFiatWithdrawal: boolean
}

type SetIsFilterLoading = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_FILTER_LOADING
  isFilterLoading: boolean
}

type SetIsPaginationLoading = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_PAGINATION_LOADING
  isPaginationLoading: boolean
}

type GetTransactions = {
  type: typeof TRANSACTIONS_ACTIONS.GET_TRANSACTIONS
  transactions: Transaction[]
  contractsExist: boolean
}

type GetTransactionsTotalCount = {
  type: typeof TRANSACTIONS_ACTIONS.GET_TRANSACTIONS_TOTAL_COUNT
  totalCount: number
}

type GetTransactionFilterOptions = {
  type: typeof TRANSACTIONS_ACTIONS.GET_TRANSACTION_FILTER_OPTIONS
  viewType: string
  filterOptions: {
    [TRANSACTIONS_TAB_KEY.XFERS_ACCOUNT]: FilterOptions
    [TRANSACTIONS_TAB_KEY.XFERS_WALLET]: FilterOptions
  }
}

type SetFilters = {
  type: typeof TRANSACTIONS_ACTIONS.SET_FILTERS
  filterValuesObject: FilterValuesObject
}

type ResetCurrentPage = {
  type: typeof TRANSACTIONS_ACTIONS.RESET_CURRENT_PAGE
}

type SetCsvFormatVersion = {
  type: typeof TRANSACTIONS_ACTIONS.SET_CSV_FORMAT_VERSION
  csvFormatVersion: CSV_FORMAT_VERSION
}

type SwitchTransactionsTab = {
  type: typeof TRANSACTIONS_ACTIONS.SWITCH_TRANSACTIONS_TAB
  transactionsTabKey: TRANSACTIONS_TAB_KEY
}

type GetTransactionTabOptions = {
  type: typeof TRANSACTIONS_ACTIONS.GET_TRANSACTION_TAB_OPTIONS
  transactionTabOptions: TRANSACTIONS_TAB_KEY[]
}

type SetLatestPersonalTransactions = {
  type: typeof TRANSACTIONS_ACTIONS.SET_LATEST_PERSONAL_TRANSACTIONS
  latestPersonalTransactions: Transaction[]
}

type SetTransactionTypeTab = {
  type: typeof TRANSACTIONS_ACTIONS.SET_TRANSACTION_TYPE_TAB
  transactionTypeTab: TRANSACTION_TYPE_TAB
}

type SetFundingTransactionTypeTab = {
  type: typeof TRANSACTIONS_ACTIONS.SET_FUNDING_TRANSACTION_TYPE_TAB
  fundingTransactionTypeTab: FUNDING_TRANSACTION_TYPE_TAB
}

type GetTravelRuleComplianceBlockerTotalCount = {
  type: typeof TRANSACTIONS_ACTIONS.GET_TRAVEL_RULE_COMPLIANCE_BLOCKER_TOTAL_COUNT
  travelRuleComplianceBlockerTotalCount: number
}

type SetIsTransactionTimezoneControlled = {
  type: typeof TRANSACTIONS_ACTIONS.SET_IS_TRANSACTION_TIMEZONE_CONTROLLED
  isTransactionTimezoneControlled: boolean
}

export type ActionTypes =
  | SetIsTxnLoading
  | SetIsFilterLoading
  | SetIsPaginationLoading
  | GetTransactions
  | GetTransactionsTotalCount
  | GetTransactionFilterOptions
  | SetFilters
  | ResetCurrentPage
  | SetCsvFormatVersion
  | SwitchTransactionsTab
  | GetTransactionTabOptions
  | SetLatestPersonalTransactions
  | SetTransactionTypeTab
  | SetFundingTransactionTypeTab
  | GetTravelRuleComplianceBlockerTotalCount
  | SetIsTransactionTimezoneControlled
  | SetIsUserHasDeposit
  | SetIsUserHasFiatDeposit
  | SetIsUserHasStablecoinDeposit
  | SetIsUserHasStablecoinWithdrawal
  | SetIsUserHasFiatWithdrawal
